import { WorkbookDomain } from '@mathflat/domain/@entities/(Content)/Workbook/domain.ts'
import { observer } from 'mobx-react'
import { createBrowserRouter, generatePath, Navigate, redirect } from 'react-router-dom'

import { routeName } from '~/@common/constants'
import { resolveRootRedirect } from '~/@common/integration/safeRedirect'
import { isWorkbookTypeAllowed } from '~/@common/models/(Permission)/permissionUtils'
import authService from '~/@common/services/auth.service'
import { commonRepo } from '~/@common/services/repo.service'
import ErrorBoundary from '~/@common/ui/AsyncBoundary/ErrorBoundary'
import ErrorFallback from '~/@common/ui/AsyncBoundary/ErrorFallback'
import { getEntrancePage } from '~/@common/utils/getEntrancePage'
import LectureLinkPage from '~/@pages/bridge/LectureLink.page'
import QRPage from '~/@pages/bridge/QR.page'
import SignatureWorkbookLectureLinkPage from '~/@pages/bridge/SignatureWorkbookLectureLink.page'
import TeacherAppBridgePage from '~/@pages/bridge/TeacherAppBridge.page'
import CasScoringPage from '~/@pages/cas/$assignedId/CasScoring.page'
import CasListPage from '~/@pages/cas/CasList.page'
import { GuideVideoPage } from '~/@pages/guide-video/GuideVideo.page'
import HomePage from '~/@pages/home/Home.page'
import IntegrationDevReset from '~/@pages/integration/IntegrationDevReset.page'
import IntegrationLogoutClear from '~/@pages/integration/IntegrationLogoutClear.page'
import IntegrationProbe from '~/@pages/integration/IntegrationProbe.page'
import IntegrationSync from '~/@pages/integration/IntegrationSync.page'
import AiepBridgePage from '~/@pages/login/aiep/AiepBridge.page'
import GuestLoginPage from '~/@pages/login/guest/GuestLogin.page'
import LoginPage from '~/@pages/login/Login.page.tsx'
import AiOnOAuthCallbackPage from '~/@pages/login/naver-works/AiOnOAuthCallback.page'
import AiOnOAuthLoginPage from '~/@pages/login/naver-works/AiOnOAuthLogin.page'
import LoginFromNaverWorksPage from '~/@pages/login/naver-works/LoginFromNaverWorks.page'
import NotificationPage from '~/@pages/notification/Notification.page'
import AttendanceHistoryPage from '~/@pages/parent/attendance/AttendanceHistory.page'
import ParentNoticePage from '~/@pages/parent/notice/ParentNotice.page'
import ParentNoticeDetailPage from '~/@pages/parent/notice/ParentNoticeDetail.page'
import ParentReportPage from '~/@pages/parent/report/ParentReport.page'
import ProfilePage from '~/@pages/profile'
import QrWorksheetScoringPage from '~/@pages/qr-worksheet/$worksheetId/$dateTime/scoring/QrWorksheetScoring.page'
import ServerMaintenance from '~/@pages/serverMaintenance'
import ChallengeEntry from '~/@pages/student/challenge/entry.tsx'
import ConceptLearningPage from '~/@pages/student/concept-learning/ConceptLearning.page'
import ConceptualLearningPage from '~/@pages/student/conceptual-learning/ConceptualLearning.page'
import LearningProcess from '~/@pages/student/learning-process/LearningProcess.page'
import LectureDetailPage from '~/@pages/student/lecture/$lectureId/LectureDetail.page'
import LectureListPage from '~/@pages/student/lecture/LectureList.page'
import SignatureWorkbookLectureListPage from '~/@pages/student/lecture/signature-workbook/$curriculumKey/SignatureWorkbookLectureList.page'
import StudentExamByIdScoringPage from '~/@pages/student-exam/$studentExamId/scoring/StudentExamByIdScoring.page'
import StudentWorkbookDetailPage from '~/@pages/student-workbook/detail/StudentWorkbookDetail.page'
import StudentWorkbookListPage from '~/@pages/student-workbook/list/StudentWorkbookList.page'
import StudentWorkbookViewerPage from '~/@pages/student-workbook/viewer/StudentWorkbookViewer.page'
import StudentWorksheetScoringPage from '~/@pages/student-worksheet/$studentWorksheetId/scoring/StudentWorksheetScoring.page'
import StudentWorksheetDetailPage from '~/@pages/student-worksheet/$studentWorksheetId/StudentWorksheetDetail.page'
import { StudentWorksheetListPage } from '~/@pages/student-worksheet/StudentWorksheetList.page'
import DraggableTestPage from '~/@pages/test/DraggableTest.page'

import PermissionGuardRoute from './(Route)/PermissionGuardRoute'
import ProtectedRoute from './(Route)/ProtectedRoute'
import App from './App'

const WorkbookDefaultRedirect = observer(() => {
  const { permission } = commonRepo
  const defaultPath = isWorkbookTypeAllowed(permission, WorkbookDomain.TYPE.커스텀시그니처교재)
    ? routeName.student.customSignatureStudentWorkbookList
    : isWorkbookTypeAllowed(permission, WorkbookDomain.TYPE.학원그룹교재)
      ? routeName.student.academyGroupStudentWorkbookList
      : routeName.student.generalStudentWorkbookList

  return <Navigate to={defaultPath} replace />
})

export interface RouteHandle {
  ignoreMobileContainerPadding?: boolean // breakpoints 기준 mobile이면 <Container />에 padding: 0 지정
  ignoreContainerPadding?: boolean // 모든 breakpoint에서 <Container />의 padding, max-width 제거
}

// type issue: https://github.com/remix-run/react-router/issues/10787
export const appRouter: ReturnType<typeof createBrowserRouter> = createBrowserRouter(
  [
    {
      element: <App />,
      ErrorBoundary: () => <ErrorBoundary FallbackComponent={ErrorFallback} />,
      loader: ({ request }) => {
        const safeRedirect = resolveRootRedirect(request.url)
        if (safeRedirect) return redirect(safeRedirect)
        return null
      },
      children: [
        {
          path: 'lecture/:lectureId',
          loader: ({ params }) => {
            return redirect(
              generatePath(routeName.student.lectureDetail, { lectureId: params.lectureId! }),
            )
          },
        },
        {
          path: routeName.login,
          element: <LoginPage />,
        },
        {
          // 선생님앱 iframe 브릿지 (ProtectedRoute 밖 — 토큰 없이 접근하는 엔트리 포인트)
          path: routeName.bridge.teacherIframe,
          element: <TeacherAppBridgePage />,
        },
        // b2g 외부 SSO/로그인 엔트리 포인트 (ProtectedRoute 밖 — 로그인 전 단계)
        // ⚠️ 경로값들은 파트너사 시스템에 등록된 redirect_uri/magic URL이므로 절대 변경 금지
        {
          path: routeName.auth.whalespace,
          element: <LoginFromNaverWorksPage />,
        },
        {
          path: routeName.auth.aiOnOAuthLogin,
          element: <AiOnOAuthLoginPage />,
        },
        {
          path: routeName.auth.aiOnOAuthCallback,
          element: <AiOnOAuthCallbackPage />,
        },
        {
          path: routeName.auth.aiepBridge,
          element: <AiepBridgePage />,
        },
        {
          path: routeName.auth.guestLogin,
          element: <GuestLoginPage />,
        },
        {
          // 구 베이직학생앱 호환: 네이티브 앱 딥링크 fallback 브릿지 (ProtectedRoute 밖 — 네이티브 앱 실행이 목적)
          path: routeName.bridge.lecture,
          element: <LectureLinkPage />,
        },
        {
          path: routeName.bridge.signatureWorkbookLecture,
          element: <SignatureWorkbookLectureLinkPage />,
        },
        {
          path: routeName.qrWorksheet.scoring,
          element: (
            <ProtectedRoute>
              <QrWorksheetScoringPage />
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.bridge.qr,
          element: <QRPage />,
        },
        // ─── 통합 마이그레이션 placeholder 라우트 ───────────────────────────────
        //   실제 로직은 모두 index.html 인라인 스크립트 (HTML parse 시점에 listener 등록 → race-safe).
        //   본 라우트들은 React 마운트 후 필요없는 API call 이나 로직을 막기 위한 빈 placeholder.
        //   - sync        : (sender→receiver 단방향) receiver 가 띄운 iframe 안에서 REQUEST_SYNC 응답 (storage 통째 송신)
        //   - logoutClear : (양방향) 명시 로그아웃 시 상대 origin 이 띄운 iframe 안에서 LOGOUT_CLEAR 응답
        //   - probe       : sender 가 띄운 invisible iframe 이 자발 PROBE_RESPONSE 송신 (상대방 배포 확인용)
        //   - devReset    : receiver dev panel 이 띄운 iframe 안에서 sender TRANSFERRED marker 제거 (라이브 외 한정)
        {
          path: routeName.integration.sync,
          element: <IntegrationSync />,
        },
        {
          path: routeName.integration.logoutClear,
          element: <IntegrationLogoutClear />,
        },
        {
          path: routeName.integration.probe,
          element: <IntegrationProbe />,
        },
        {
          path: routeName.integration.devReset,
          element: <IntegrationDevReset />,
        },
        {
          path: routeName.guideVideo,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <GuideVideoPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.notification,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <NotificationPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: '/auth/*',
          element: (
            <ProtectedRoute>
              <Navigate to={getEntrancePage(authService.userType)} />
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.student.defaultPath,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <HomePage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.studentWorksheet,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentWorksheetListPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.studentWorksheetById,
          element: (
            <ProtectedRoute>
              <StudentWorksheetDetailPage />
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.student.studentWorksheetScoringById,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentWorksheetScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.studentExamScoringById,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentExamByIdScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.cas,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <CasListPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.casByAssignedId,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <CasScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.challenge,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <ChallengeEntry />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.studentWorkbook,
          children: [
            {
              index: true,
              element: (
                <ProtectedRoute>
                  <WorkbookDefaultRedirect />
                </ProtectedRoute>
              ),
            },
            {
              path: routeName.student.studentWorkbookList,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <StudentWorkbookListPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
              handle: {
                ignoreMobileContainerPadding: true,
              },
            },

            {
              path: routeName.student.studentWorkbookDetail,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <StudentWorkbookDetailPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
              handle: {
                ignoreMobileContainerPadding: true,
              },
            },
            {
              path: routeName.student.studentWorkbookViewer,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <StudentWorkbookViewerPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
              handle: {
                ignoreMobileContainerPadding: true,
              },
            },
            {
              path: '*',
              element: (
                <ProtectedRoute>
                  <Navigate to={routeName.student.generalStudentWorkbookList} replace />
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: routeName.student.lecture,
          children: [
            { index: true, element: <Navigate to={routeName.student.lectureList} /> },
            {
              path: routeName.student.lectureList,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <LectureListPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
            },
            {
              path: routeName.student.assignedLectureDetail,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <LectureDetailPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
              handle: {
                ignoreMobileContainerPadding: true,
              },
            },
            {
              path: routeName.student.lectureDetail,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <LectureDetailPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
              handle: {
                ignoreMobileContainerPadding: true,
              },
            },
            {
              path: routeName.student.signatureWorkbookLectureList,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <SignatureWorkbookLectureListPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: routeName.student.conceptLearning, // 삭제 예정 (진입 시 conceptualLearning으로 redirect)
          element: (
            <ProtectedRoute>
              <ConceptLearningPage />
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.conceptualLearning,
          element: (
            <ProtectedRoute>
              <ConceptualLearningPage />
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.profile,
          element: (
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.parent.defaultPath,
          element: (
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.attendance,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <AttendanceHistoryPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreContainerPadding: true,
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.report,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <ParentReportPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.parent.notice,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <ParentNoticePage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.parent.noticeDetail,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <ParentNoticeDetailPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.studentWorksheetScoringById,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentWorksheetScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.studentExamScoringById,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentExamByIdScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.studentWorkbookViewer,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentWorkbookViewerPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.studentWorkbookDetail,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentWorkbookDetailPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.studentLearningProcess,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <LearningProcess />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.serverMaintenance,
          element: <ServerMaintenance />,
        },
        {
          path: '/test/draggable',
          element: <DraggableTestPage />,
        },
        // 매칭되지 않는 모든 경로(/, 오타, 삭제된 경로 등) catch-all
        {
          path: '*',
          loader: () => {
            if (authService.isLoggedIn) {
              return redirect(getEntrancePage(authService.userType))
            }
            return redirect(routeName.login)
          },
        },
      ],
    },
  ],
  {
    future: {
      v7_normalizeFormMethod: true,
      v7_fetcherPersist: true,
    },
  },
)

export type AppRouter = typeof appRouter
